import ValueCard from '../connectedForm/ValueCard';
import titleCase from '../../utils/TitleCase';
import Row from '../connectedForm/Row';
import React, { ComponentProps, CSSProperties, JSXElementConstructor, ReactNode, useContext } from 'react';
import styled from 'styled-components';
import { IAggregateColumn, IAggregateRow } from '../../graphql/schema/p4types';
import { EntityType } from '../../@types/entityTypes';
import WorkspaceContext from '../Context/WorkspaceContext';
import { Tooltip } from '@fluentui/react-components';
import type { Slot } from '@fluentui/react-utilities';

const CardContainer = styled.div<{ background?: string }>`
  box-sizing: border-box;
  min-width: 120px;
  height: 55px;
  display: inline-block;
  border-bottom: 2px solid #eee;
  border-radius: 3px;
  background: ${(props) => props.background || '#f5f5f5'};
  margin: 10px;
`;

export const CustomTotalCard = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: ComponentProps<typeof Tooltip>['content'];
}) => {
  const { embeddedView } = useContext(WorkspaceContext);

  const card = (
    <CardContainer
      key={`customCard_${label}`}
      background={embeddedView ? '#ffffff' : undefined}
      style={{ borderBottom: '3px solid #D0EDFF' }}
    >
      <ValueCard
        primary={true}
        size="large"
        currency={false /*False for now - Not sure how to determine which columns are Currency*/}
        decimals={2}
        label={titleCase(label)}
        value={value}
      />
    </CardContainer>
  );

  if (hint) {
    return (
      <Tooltip relationship={'label'} content={hint}>
        {card}
      </Tooltip>
    );
  }

  return card;
};

//listData is partial, just lists the required elements.
type ITotalViewProps = {
  listData: {
    count: number;
    totalColumns: Array<IAggregateColumn>;
    aggregateRows?: Array<IAggregateRow>;
  };
  embeddedView: boolean;
  additionalCards?: Array<{ label: string; value: number }> | JSXElementConstructor<any>;
  entity: EntityType;
  contentWidth?: number | string;
};

export const TotalsView = ({ listData, embeddedView, additionalCards, entity, contentWidth }: ITotalViewProps) => {
  //If there are aggregate rows, skip the totals view, since it is superceded by the 2-dimensional bar view.
  if (
    !(
      (listData?.totalColumns?.length > 0 || additionalCards) &&
      (!listData?.aggregateRows || listData?.aggregateRows?.length === 0)
    )
  ) {
    return null;
  }
  const cards =
    listData?.totalColumns?.map((totalColumn) => {
      return (
        <CardContainer key={totalColumn.columnName as string} background={embeddedView ? '#ffffff' : undefined}>
          <ValueCard
            primary={true}
            size="large"
            currency={false /*False for now - Not sure how to determine which columns are Currency*/}
            decimals={2}
            label={titleCase(totalColumn.columnName as string)}
            value={totalColumn.sum}
          />
        </CardContainer>
      );
    }) ?? [];
  const countTitle = entity?.title || 'Count';
  return (
    <div
      style={{
        width: contentWidth ? `${contentWidth}px` : '100%',
        paddingLeft: '10px',
        borderBottomWidth: '1px',
        borderBottomColor: '#e1e1e1',
        borderBottomStyle: 'solid',
      }}
    >
      <Row>
        <CardContainer key={`total_count`} background={embeddedView ? '#ffffff' : undefined}>
          <ValueCard size="large" label={countTitle} primary={true} value={listData?.count ?? 0} />
        </CardContainer>
        {cards}
        {additionalCards}
      </Row>
    </div>
  );
};
